import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useWalletBalance, useUsageSummary, useUsageLogs, useUsageDashboards, useUsageActivity, useApiKeys, useWalletPayments, useUsdRate, useSubscription, useSubscriptionUsage } from "../hooks/useApi";
import Card from "../components/Card";
import DataTable from "../components/DataTable";
import Badge from "../components/Badge";
import StackedAreaChart, { type DashboardUsageData } from "../components/StackedAreaChart";
import RadialBarChart from "../components/RadialBarChart";
import ActivityHeatmap from "../components/ActivityHeatmap";
import InvoiceDrawer from "../components/InvoiceDrawer";
import FixedUsageDrawer from "../components/FixedUsageDrawer";
import FixedPlanIcon from "../components/FixedPlanIcon";
import { formatToman, formatTomanInt, formatNumber, relativeTime, toPersianDigits } from "../utils/format";
import {
  DASHBOARD_DURATIONS,
  DASHBOARD_METRICS,
  DASHBOARD_DURATION_STORAGE_KEY,
  loadDashboardDuration,
  type DashboardMetric,
} from "../utils/usage";

const inputClass =
  "h-9 px-3 rounded-btn border border-line text-small text-text-secondary bg-surface focus:outline-none focus:ring-2 focus:ring-primary/30";

export default function Dashboard() {
  const navigate = useNavigate();
  const { data: wallet } = useWalletBalance();
  const { data: usdData } = useUsdRate();
  const { data: summary } = useUsageSummary();
  const { data: logs, isLoading: logsLoading } = useUsageLogs({ limit: 10 });
  const { data: apiKeys, isLoading: keysLoading } = useApiKeys();
  const { data: payments, isLoading: paymentsLoading } = useWalletPayments();
  const { data: subscription } = useSubscription();
  const { data: subscriptionUsage } = useSubscriptionUsage(Boolean(subscription?.current_fixed));

  const fixedSub = subscription?.current_fixed as Record<string, unknown> | null | undefined;
  const fixedUsage = subscriptionUsage as Record<string, unknown> | null | undefined;
  const fixedEndDate = fixedSub?.end_date as string | undefined;
  const fixedDaysLeft = fixedEndDate
    ? Math.max(0, Math.ceil((new Date(fixedEndDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;

  const [duration, setDuration] = useState<string>(loadDashboardDuration);
  const [metric, setMetric] = useState<DashboardMetric>("tokens");
  const [selectedPayment, setSelectedPayment] = useState<Record<string, unknown> | null>(null);
  const [fixedDrawerOpen, setFixedDrawerOpen] = useState(false);
  const { byMetric: dashboardByMetric } = useUsageDashboards(duration);
  // All metrics for the selected duration are prefetched together, so switching
  // tabs reads from cache instantly instead of triggering a fresh fetch + skeleton flash.
  const dashboardData = dashboardByMetric[metric] as DashboardUsageData | undefined;
  const { data: activity, isLoading: activityLoading } = useUsageActivity();

  // Persist the user's preferred dashboard duration
  useEffect(() => {
    localStorage.setItem(DASHBOARD_DURATION_STORAGE_KEY, duration);
  }, [duration]);

  const statusBadge = (status: string) => {
    switch (status) {
      case "completed": return <Badge variant="success">موفق</Badge>;
      case "failed": return <Badge variant="error">ناموفق</Badge>;
      case "forwarded": return <Badge variant="warning">در حال پردازش</Badge>;
      default: return <Badge variant="info">{status}</Badge>;
    }
  };

  const columns = [
    { key: "model", header: "مدل", render: (item: Record<string, unknown>) => <span className="font-number block text-small">{item.model as string}</span> },
    { key: "endpoint", header: "نوع", render: (item: Record<string, unknown>) => <span className="text-small text-text-tertiary">{item.endpoint as string}</span> },
    { key: "cost", header: "هزینه (تومان)", render: (item: Record<string, unknown>) => <span className="font-number">{formatToman(item.cost as number)}</span> },
    { key: "status", header: "وضعیت", render: (item: Record<string, unknown>) => statusBadge(item.status as string) },
    { key: "created_at", header: "زمان", render: (item: Record<string, unknown>) => {
      const d = new Date(item.created_at as string);
      return <span className="text-small text-text-tertiary">{d.toLocaleDateString("fa-IR")}</span>;
    }},
  ];

  const durationLabel = DASHBOARD_DURATIONS.find((d) => d.value === duration)?.label || "";
  const metricLabel = DASHBOARD_METRICS.find((m) => m.value === metric)?.label || "";

  const keys = (apiKeys || []) as Record<string, unknown>[];
  const activeKeys = keys.filter((k) => k.status === "active");
  const inactiveKeys = keys.filter((k) => k.status !== "active");
  const newestKey = keys[0];
  const keyColumns = [
    {
      key: "name",
      header: "نام",
      render: (item: Record<string, unknown>) => (
        <span className="font-number block text-small text-text-main">{item.name as string || <span className="text-text-tertiary">بدون نام</span>}</span>
      ),
    },
    {
      key: "key_prefix",
      header: "کلید",
      render: (item: Record<string, unknown>) => (
        <span dir="ltr" className="font-mono text-small text-text-main tracking-tight">{item.key_prefix as string}</span>
      ),
    },
    {
      key: "created_at",
      header: "زمان ساخت",
      render: (item: Record<string, unknown>) => {
        const date = item.created_at as string;
        return (
          <span className="text-small text-text-tertiary" title={new Date(date).toLocaleDateString("fa-IR", { year: "numeric", month: "long", day: "numeric" })}>
            {relativeTime(date)}
          </span>
        );
      },
    },
  ];

  const paymentColumns = [
    {
      key: "id",
      header: "کد",
      render: (item: Record<string, unknown>) => (
        <span className="font-number text-small text-text-tertiary" dir="ltr">
          {String(item.id)}
        </span>
      ),
    },
    {
      key: "amount",
      header: "مبلغ (تومان)",
      render: (item: Record<string, unknown>) => (
        <span className="font-number text-text-main">
          {formatToman(item.amount as number)}
        </span>
      ),
    },
    {
      key: "status",
      header: "وضعیت",
      render: (item: Record<string, unknown>) => {
        switch (item.status) {
          case "verified": return <Badge variant="success">تأیید شده</Badge>;
          case "pending": return <Badge variant="warning">در انتظار</Badge>;
          case "failed": return <Badge variant="error">ناموفق</Badge>;
          case "expired": return <Badge variant="warning">منقضی شده</Badge>;
          default: return <Badge variant="info">{item.status as string}</Badge>;
        }
      },
    },
    {
      key: "ref_id",
      header: "کد رهگیری",
      render: (item: Record<string, unknown>) => (
        <span className="font-number text-small text-text-tertiary" dir="ltr">
          {(item.ref_id as string) || "—"}
        </span>
      ),
    },
    {
      key: "status_code",
      header: "کد وضعیت",
      render: (item: Record<string, unknown>) => (
        <span className="font-number text-small text-text-tertiary" dir="ltr">
          {(item.status_code as string) || "—"}
        </span>
      ),
    },
    {
      key: "created_at",
      header: "تاریخ",
      render: (item: Record<string, unknown>) => (
        <span className="text-small text-text-tertiary">
          {new Date(item.created_at as string).toLocaleDateString("fa-IR")}
        </span>
      ),
    },
    {
      key: "actions",
      header: "عملیات",
      render: (item: Record<string, unknown>) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setSelectedPayment(item);
          }}
          className="text-small text-primary hover:underline transition-colors"
        >
          {item.status === "verified" ? "مشاهده فاکتور" : "مشاهده جزییات"}
        </button>
      ),
    },
  ];

  const dData = (dashboardData || {}) as DashboardUsageData;
  const chartLoading = !dashboardData;

  const summaryText = (() => {
    const s = dData.summary;
    if (!s) return null;
    const tokens = formatNumber(s.tokens);
    const spend = formatTomanInt(s.spend);
    const requests = formatNumber(s.requests);
    if (metric === "spend")
      return <span>در {durationLabel}، مجموعاً <span className="text-primary font-bold">{spend}</span> تومان هزینه شده است.</span>;
    if (metric === "requests")
      return <span>در {durationLabel}، مجموعاً <span className="text-primary font-bold">{requests}</span> درخواست ثبت شده است.</span>;
    return <span>در {durationLabel}، مجموعاً <span className="text-primary font-bold">{tokens}</span> توکن مصرف شده است.</span>;
  })();

  return (
    <div className="space-y-6">
      {/* Page Header — USD rate top left (both mobile & desktop) */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-h1 text-text-main">داشبورد</h1>
          <p className="text-body text-text-secondary mt-1">خلاصه فعالیت و مصرف API</p>
        </div>
        <div className="flex items-center gap-2 px-3.5 py-2 rounded-full bg-surface border border-line text-small text-text-secondary shrink-0 shadow-sm">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <line x1="12" y1="1" x2="12" y2="23" />
            <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
          </svg>
          <span className="text-text-tertiary">نرخ دلار</span>
          <span className="font-number font-medium text-text-main">
            {usdData ? formatNumber(usdData.usd_rate_toman) : "—"}
          </span>
          <span className="text-text-tertiary">تومان</span>
        </div>
      </div>

      {/* Fixed-cost subscription status + remaining monthly tokens */}
      {fixedSub && (
        <div className="space-y-4">
          <Card>
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: "rgb(var(--chart-blue) / 0.15)", color: "rgb(var(--chart-blue))" }}
              >
                <FixedPlanIcon />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-small text-text-tertiary mb-1">اشتراک تعرفه ثابت</p>
                <p className="text-h3 font-semibold text-text-main">{toPersianDigits(fixedSub.plan_name as string)}</p>
              </div>
              <div className="text-left shrink-0">
                <p className="text-small text-text-tertiary mb-1">روزهای باقی</p>
                <p className="text-h2 font-semibold font-number text-text-main">
                  {fixedDaysLeft !== null ? toPersianDigits(String(fixedDaysLeft)) : "—"}
                </p>
              </div>
            </div>
          </Card>

          {fixedUsage && (
            <Card>
              <div className="flex items-center justify-between gap-2">
                <p className="text-small text-text-tertiary mb-1">سقف توکن ماهانه (دوره ۳۰ روز)</p>
                <button
                  type="button"
                  onClick={() => setFixedDrawerOpen(true)}
                  className="text-small text-primary font-bold hover:underline transition-colors shrink-0"
                >
                  مشاهده جزئیات
                </button>
              </div>
              <p className="text-[24px] font-semibold font-number text-text-main text-left">
                {formatNumber((fixedUsage as { remaining_tokens?: unknown }).remaining_tokens as number)}
                <span className="text-base text-text-tertiary font-normal mr-1">توکن باقیمانده</span>
              </p>
              {(() => {
                const limit = Number((fixedUsage as { monthly_token_limit?: unknown }).monthly_token_limit) || 0;
                const used = Number((fixedUsage as { used_tokens?: unknown }).used_tokens) || 0;
                const pct = Math.max(0, Math.min(100, Number((fixedUsage as { used_percent?: unknown }).used_percent) || (limit ? (used / limit * 100) : 0)));
                return (
                  <div className="mt-3">
                    <div className="h-2.5 rounded-full overflow-hidden" style={{ background: "rgb(var(--chart-blue) / 0.15)" }}>
                      <div className="h-full" style={{ width: `${Math.round(pct)}%`, background: pct >= 90 ? "#dc2626" : "rgb(var(--chart-blue))" }} />
                    </div>
                    <p className="text-small text-text-tertiary mt-1.5">
                      {formatNumber(used)} از {formatNumber(limit)} توکن مصرف شده
                    </p>
                  </div>
                );
              })()}
            </Card>
          )}
        </div>
      )}

      {/* KPI Cards */}
      {fixedSub && (
        <div className="border-t border-line pt-5">
          <div className="flex items-center gap-2">
            <span className="w-1.5 self-stretch rounded-full" style={{ background: "rgb(var(--chart-blue))" }} />
            <h2 className="text-h2 text-text-main">آمار مصرف pay as you go</h2>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <Card>
          <p className="text-small text-text-tertiary mb-1">موجودی کیف پول</p>
          <p className="text-[28px] font-semibold text-text-main font-number">
            {formatTomanInt(wallet?.balance)}
            <span className="text-base text-text-tertiary font-normal mr-1">تومان</span>
          </p>
        </Card>

        <Card>
          <p className="text-small text-text-tertiary mb-1">درخواست‌های امروز</p>
          <p className="text-[28px] font-semibold text-text-main font-number">
            {formatNumber(summary?.today_requests)}
          </p>
          <p className="text-small text-text-tertiary mt-2">
            هزینه امروز: <span className="font-number text-text-main">{formatTomanInt(summary?.today_cost)}</span> تومان
          </p>
        </Card>

        <Card>
          <p className="text-small text-text-tertiary mb-1">مجموع درخواست‌ها</p>
          <p className="text-[28px] font-semibold text-text-main font-number">
            {formatNumber(summary?.total_requests)}
          </p>
          <p className="text-small text-text-tertiary mt-2">
            هزینه کل: <span className="font-number text-text-main">{formatTomanInt(summary?.total_cost)}</span> تومان
          </p>
        </Card>

        <Card>
          <p className="text-small text-text-tertiary mb-1">مجموع پس‌انداز</p>
          <p className="text-[28px] font-semibold text-text-main font-number">
            {formatTomanInt(summary?.total_customer_caching_discount)}
            <span className="text-base text-text-tertiary font-normal mr-1">تومان</span>
          </p>
          <p className="text-small text-text-tertiary mt-2">
            سهم پس‌انداز حاصل از <span className="font-bold text-text-main">کشینگ هوشمند</span>
          </p>
        </Card>
      </div>

      {/* Usage Summary + Filters */}
      <Card padding="md">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-h2 text-text-main mb-1.5">خلاصه مصرف</h2>
            <p className="text-body text-text-secondary">{summaryText || "در حال بارگذاری…"}</p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* Metric tabs */}
            <div className="flex items-center gap-1 p-1 rounded-btn bg-surface-hover">
              {DASHBOARD_METRICS.map((m) => (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => setMetric(m.value as DashboardMetric)}
                  className={`px-3 py-1.5 rounded-btn text-small transition-colors ${
                    metric === m.value
                      ? "bg-surface text-primary font-semibold shadow-sm"
                      : "text-text-secondary hover:text-text-main"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>

            {/* Duration select */}
            <select
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              className={inputClass}
            >
              {DASHBOARD_DURATIONS.map((d) => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2">
            <h3 className="text-h3 text-text-main font-bold mb-3">
              مصرف به تفکیک مدل — {metricLabel}
            </h3>
            <StackedAreaChart
              data={dData}
              duration={duration}
              metric={metric}
              loading={chartLoading}
            />
          </div>

          <div>
            <h3 className="text-h3 text-text-main font-bold mb-3">مدل‌های برتر</h3>
            <RadialBarChart data={dData} metric={metric} duration={duration} loading={chartLoading} />
          </div>
        </div>
      </Card>

      {/* Activity */}
      <Card padding="md">
        <div className="mb-5">
          <h2 className="text-h2 text-text-main mb-1.5">فعالیت</h2>
          <p className="text-body text-text-secondary">هزینه روزانه در کل سابقه — سلول‌های تیره‌تر یعنی روزهای پرمصرف‌تر</p>
        </div>
        <ActivityHeatmap data={activity} loading={activityLoading} />
      </Card>

      {/* API Keys */}
      <Card padding="md">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-h2 text-text-main mb-1.5">کلیدهای API</h2>
            <p className="text-body text-text-secondary">نمای کلی کلیدهای دسترسی شما</p>
          </div>
          <a href="/api-keys" className="text-small text-primary hover:underline shrink-0">مشاهده همه</a>
        </div>

        {/* Overview */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <div className="rounded-btn bg-surface-base p-3">
            <p className="text-small text-text-tertiary mb-1">کلیدهای فعال</p>
            <p className="text-h2 text-text-main font-number">{formatNumber(activeKeys.length)}</p>
          </div>
          <div className="rounded-btn bg-surface-base p-3">
            <p className="text-small text-text-tertiary mb-1">لغو شده</p>
            <p className="text-h2 text-text-main font-number">{formatNumber(inactiveKeys.length)}</p>
          </div>
          <div className="rounded-btn bg-surface-base p-3">
            <p className="text-small text-text-tertiary mb-1">آخرین ساخت</p>
            <p className="text-h2 text-text-main font-number">
              {newestKey ? relativeTime(newestKey.created_at as string) : "—"}
            </p>
          </div>
        </div>

        {/* Keys Table */}
        <DataTable
          columns={keyColumns}
          data={keys.slice(0, 5)}
          loading={keysLoading}
          emptyMessage="هنوز هیچ کلید API ای ساخته نشده است"
          onRowClick={() => navigate("/api-keys")}
        />
      </Card>

      {/* Payment History */}
      <Card padding="md">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-h2 text-text-main">تاریخچه پرداخت‌ها</h2>
          <a href="/wallet" className="text-small text-primary hover:underline">مشاهده همه</a>
        </div>
        <DataTable
          columns={paymentColumns}
          data={(payments || []) as Record<string, unknown>[]}
          loading={paymentsLoading}
          emptyMessage="هیچ پرداختی ثبت نشده است"
        />
      </Card>

      {/* Recent Activity */}      <Card padding="md">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-h2 text-text-main">فعالیت‌های اخیر</h2>
          <a href="/usage" className="text-small text-primary hover:underline">مشاهده همه</a>
        </div>
        <DataTable
          columns={columns}
          data={(logs || []) as Record<string, unknown>[]}
          loading={logsLoading}
          emptyMessage="هیچ درخواست API ای ثبت نشده است"
        />
      </Card>

      {/* Invoice Drawer */}
      <InvoiceDrawer selectedPayment={selectedPayment} onClose={() => setSelectedPayment(null)} />

      {/* Fixed-rate usage drawer */}
      <FixedUsageDrawer
        open={fixedDrawerOpen}
        onClose={() => setFixedDrawerOpen(false)}
        fixedSub={fixedSub}
        fixedUsage={fixedUsage}
      />
    </div>
  );
}
